/**
 * User detail page.
 * 
 * @module pages/UserDetailPage
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, CreditCard, Building2 } from 'lucide-react';
import { Card, Badge, LoadingSection, ErrorState, EmptyState } from '../components/ui';
import { useUser, useUserBusinesses, useAuditLog, useExclusionRules } from '../hooks';
import { isExcludedUser } from '../config/excluded-users';
import { AuditActionTypes, AuditResourceTypes } from '../lib/audit';
import { formatDate, formatDateTime, getInitials, cn } from '../lib/utils';

/**
 * User detail page component.
 * 
 * @returns User detail page element
 */
export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { logAction } = useAuditLog();

  const [businessFilter, setBusinessFilter] = useState<'external' | 'all'>('external');

  const { data: user, isLoading, error } = useUser(id);
  const { data: businesses, isLoading: businessesLoading } = useUserBusinesses(id);
  const { data: exclusionRules } = useExclusionRules();

  // Log page view
  useEffect(() => {
    if (user) {
      logAction(
        AuditActionTypes.VIEW_USER,
        AuditResourceTypes.USER,
        user.id,
        { email: user.email, full_name: user.full_name }
      );
    }
  }, [user, logAction]);

  if (isLoading) {
    return <LoadingSection text="Loading user details..." />;
  }

  if (error || !user) {
    return (
      <ErrorState
        message={error?.message || 'User not found'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // All businesses on this page belong to the same user, so the
  // internal/external filter is all-or-nothing per user.
  const isInternalUser = isExcludedUser(user.email, exclusionRules);
  const filteredBusinesses = businessFilter === 'external' && isInternalUser
    ? []
    : businesses;

  const statusVariant =
    user.subscription_status === 'active' ? 'success' :
    user.subscription_status === 'trialing' ? 'info' :
    user.subscription_status === 'past_due' ? 'warning' :
    user.subscription_status === 'canceled' ? 'error' :
    'neutral';

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/users"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Link>

      {/* User header */}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-xl font-semibold text-primary-700">
            {getInitials(user.full_name)}
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {user.full_name || 'Unnamed User'}
          </h1>
          <p className="text-gray-500">{user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User info card */}
        <Card title="User Information" className="lg:col-span-2">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email
              </dt>
              <dd className="mt-1 text-gray-900">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Joined
              </dt>
              <dd className="mt-1 text-gray-900">{formatDateTime(user.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Full Name</dt>
              <dd className="mt-1 text-gray-900">{user.full_name || '—'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-gray-900">{formatDateTime(user.updated_at)}</dd>
            </div>
          </dl>
        </Card>

        {/* Subscription card */}
        <Card title="Subscription">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Plan</dt>
              <dd className="mt-1">
                <Badge variant="info">{user.plans?.name || 'No plan'}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <Badge variant={statusVariant}>
                  {user.subscription_status || 'None'}
                </Badge>
              </dd>
            </div>
            {user.stripe_customer_id && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Stripe Customer
                </dt>
                <dd className="mt-1 text-sm text-gray-600 font-mono">
                  {user.stripe_customer_id}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      </div>

      {/* User's businesses */}
      <Card
        title={`Businesses (${filteredBusinesses?.length || 0})`}
        action={
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
              <button
                onClick={() => setBusinessFilter('external')}
                className={cn(
                  'px-2.5 py-1 rounded-md font-medium transition-colors',
                  businessFilter === 'external'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                External
              </button>
              <button
                onClick={() => setBusinessFilter('all')}
                className={cn(
                  'px-2.5 py-1 rounded-md font-medium transition-colors',
                  businessFilter === 'all'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                All
              </button>
            </div>
            <Link
              to={`/businesses?owner=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          </div>
        }
      >
        {businessesLoading ? (
          <LoadingSection text="Loading businesses..." />
        ) : filteredBusinesses && filteredBusinesses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Industry</th>
                  <th>Website</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredBusinesses.map((business) => (
                  <tr key={business.id} className="hover:bg-gray-50">
                    <td>
                      <Link
                        to={`/businesses/${business.id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {business.business_name}
                      </Link>
                    </td>
                    <td>{business.industry || '—'}</td>
                    <td>
                      {business.website_url ? (
                        <a
                          href={business.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-secondary-600 hover:text-secondary-700"
                        >
                          {new URL(business.website_url).hostname}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-gray-500">{formatDate(business.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : businessFilter === 'external' && isInternalUser && businesses && businesses.length > 0 ? (
          <EmptyState
            title="Internal user"
            description={`This user is internal. Switch to "All" to see their ${businesses.length} business${businesses.length !== 1 ? 'es' : ''}.`}
            icon={<Building2 className="h-8 w-8 text-gray-400" />}
          />
        ) : (
          <EmptyState
            title="No businesses"
            description="This user hasn't created any businesses yet."
            icon={<Building2 className="h-8 w-8 text-gray-400" />}
          />
        )}
      </Card>
    </div>
  );
}
